import React, { useEffect, useState } from 'react';
import './AdminMarketplace.scss';
import Layout from '../../components/Layout';
import { Breadcrumb, BreadcrumbItem, Card } from 'reactstrap';
import { HomeButton } from '../../components/HomeButton/home-button';
import { useStateSelector } from '../../store/selectors';
import { USER_ROLES } from '../../utils/constants';
import { NotificationFlag } from '../../components/NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../models/NotificationFlags/NotificationFlagsCategories';
import TimelineTemplates from './MarketplaceTabs/TimelineTemplates/TimelineTemplates';
import { MarketplaceBuyers } from './MarketplaceTabs/MarketplaceBuyers/MarketplaceBuyers';
import Engagements from './MarketplaceTabs/Engagements/Engagements';
import { useSearchParams } from 'react-router-dom';
import DocumentTemplates from './MarketplaceTabs/DocumentTemplates/DocumentTemplates';
import {
    AdminMarketplaceTab,
    AdminMarketplaceTabs,
} from '../../slices/marketplace/timeline/models/enums/admin-marketplace-tab.enum';
import MarketplaceSettings from './MarketplaceTabs/MarketplaceSettings/MarketplaceSettings';
import { useFeatureFlag } from '../../custom-hooks/useFeatureFlag';
import { FeatureFlagsEnum } from '../FeatureFlags/FeatureFlags.enum';
import { MarketplaceBrokers } from './MarketplaceTabs/MarketplaceBrokers/MarketplaceBrokers';
import MarketplaceBuyerNdas from './MarketplaceTabs/MarketplaceBuyerNdas/MarketplaceBuyerNdas';
import SupportMeetings from './MarketplaceTabs/SupportMeetings/SupportMeetings';
import { LegalIssuesGuidance } from './MarketplaceTabs/LegalIssuesGuidance/LegalIssuesGuidance';
import { AdditionalNdaCovenants } from './MarketplaceTabs/AdditionalNdaCovenants/AdditionalNdaCovenants';
import { MarketplaceFaqs } from './MarketplaceTabs/AdminFaqs/AdminFaqs';

const AdminMarketplace = () => {
    const tabComponents = [
        { id: AdminMarketplaceTab.Engagements, content: () => <Engagements /> },
        {
            id: AdminMarketplaceTab.TimelineTemplates,
            content: () => <TimelineTemplates />,
        },
        {
            id: AdminMarketplaceTab.MarketplaceBuyers,
            content: () => <MarketplaceBuyers />,
        },
        {
            id: AdminMarketplaceTab.MarketplaceBrokers,
            content: () => <MarketplaceBrokers />,
        },
        {
            id: AdminMarketplaceTab.DocumentTemplates,
            content: () => <DocumentTemplates />,
        },
        {
            id: AdminMarketplaceTab.MarketplaceBuyerNdas,
            content: () => <MarketplaceBuyerNdas />,
        },
        {
            id: AdminMarketplaceTab.MarketplaceSettings,
            content: () => <MarketplaceSettings isTemplate={true} />,
        },
        {
            id: AdminMarketplaceTab.SupportMeetings,
            content: () => <SupportMeetings />,
        },
        {
            id: AdminMarketplaceTab.LegalIssuesGuidance,
            content: () => <LegalIssuesGuidance />,
        },
        {
            id: AdminMarketplaceTab.AdditionalNdaCovenants,
            content: () => <AdditionalNdaCovenants />,
        },
        {
            id: AdminMarketplaceTab.FAQs,
            content: () => <MarketplaceFaqs isAdmin={true} />,
        },
    ];
    const isSellers28Implemented = useFeatureFlag(
        FeatureFlagsEnum.FF_MrktplcSellers_2_8
    );
    const isFF_MrktplcBuyers_3_13Enabled = useFeatureFlag(
        FeatureFlagsEnum.FF_MrktplcBuyers_3_13
    );
    const isFF_MrktplcSellers_2_15Enabled = useFeatureFlag(
        FeatureFlagsEnum.FF_MrktplcSellers_2_15
    );

    const [searchParams, setSearchParams] = useSearchParams({});
    const userSummary = useStateSelector((state) => state.userSummary.summary);
    const isAdmin = userSummary.roleId === USER_ROLES.ADMIN;

    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        const handleNavigateToMeetings = (event: any) => {
            const { eventId } = event.detail;

            setActiveTab(AdminMarketplaceTab.SupportMeetings);
            setSearchParams({
                tab: AdminMarketplaceTab.SupportMeetings.toString(),
                selectedMeeting: eventId.toString(),
            });
        };

        window.addEventListener('navigateToMeetings', handleNavigateToMeetings);

        return () => {
            window.removeEventListener(
                'navigateToMeetings',
                handleNavigateToMeetings
            );
        };
    }, [setSearchParams]);

    useEffect(() => {
        const value = searchParams.get('tab');
        const numberValue = Number(value);
        if (
            value &&
            numberValue &&
            tabComponents.some((tab) => tab.id === numberValue)
        ) {
            setActiveTab(numberValue);
        } else {
            setSearchParams({
                tab: tabComponents[0].id.toString(),
            });
        }
    }, [searchParams]);

    const renderTabContent = () => {
        const activeTabData = tabComponents.find((tab) => tab.id === activeTab);
        return activeTabData ? activeTabData.content() : <></>;
    };

    const renderTabs = () => {
        return (
            <Card className="main-card">
                <div className="body_white_box practice-page-tabs m-0">
                    <div className="body_inner_content">
                        <div className="RRT__container">
                            <div className="RRT__tabs body-tabs">
                                {AdminMarketplaceTabs?.filter(
                                    (t) =>
                                        (isSellers28Implemented ||
                                            t.id !==
                                                AdminMarketplaceTab.MarketplaceBrokers) &&
                                        (isFF_MrktplcBuyers_3_13Enabled ||
                                            (t.id !==
                                                AdminMarketplaceTab.LegalIssuesGuidance && t.id !== AdminMarketplaceTab.AdditionalNdaCovenants)) &&
                                        (isFF_MrktplcSellers_2_15Enabled ||
                                            t.id !== AdminMarketplaceTab.FAQs)
                                ).map((item) => {
                                    return (
                                        <div
                                            key={item.id}
                                            className={
                                                activeTab === item.id
                                                    ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                                    : 'RRT__tab'
                                            }
                                            onClick={() => {
                                                setActiveTab(item.id);
                                                setSearchParams({
                                                    tab: item.id.toString(),
                                                });
                                            }}>
                                            {item.content}
                                            {item.id === AdminMarketplaceTab.Engagements && (
                                                <NotificationFlag
                                                    doNotRemove={true}
                                                    flagClassName="notification-flag-wrapper"
                                                    eventCode=""
                                                    matchPattern={(code) =>
                                                        code
                                                            .toLowerCase()
                                                            .includes(
                                                                NotificationFlagsCategories.SellerProfileUpdatedCode.toLowerCase()
                                                            )
                                                    }
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        {renderTabContent()}
                    </div>
                </div>
            </Card>
        );
    };

    const renderMarketplacePage = () => {
        return (
            <div className="admin-marketplace">
                <div>
                    <div className="main-title-content">
                        <div className="title-breadcrumb">
                            <div className="page-title">
                                <h3>Marketplace Admin</h3>
                            </div>
                            <Breadcrumb>
                                <BreadcrumbItem>
                                    <HomeButton />
                                </BreadcrumbItem>
                                <BreadcrumbItem active>
                                    Marketplace Admin Home
                                </BreadcrumbItem>
                            </Breadcrumb>
                        </div>
                    </div>
                </div>

                {renderTabs()}
            </div>
        );
    };

    return (
        <Layout>
            {isAdmin ? (
                renderMarketplacePage()
            ) : (
                <h1>You don't have an access to see this page.</h1>
            )}
        </Layout>
    );
};

export default AdminMarketplace;
